import React, { useState, useCallback } from 'react';
import MediaRenderer from './MediaRenderer';
import { mediaAPI } from '../../../services/api';

const serverBaseURL = window.location.origin.replace(':3000', ':8000');

const AiMedia = ({
  pageSize = 20,
  gridCols = 2,
  className = '',
  onItemDoubleClick,
  searchQuery,
}) => {
  const fetchAiMedia = useCallback(
    async (page, pageSize) => {
      try {
        const response = await mediaAPI.getAiMedia({
          page,
          limit: pageSize,
          search: searchQuery,
        });

        const media = response.data?.data?.media || [];
        const currentPage = response.data?.page || 0;
        const totalPages = response.data?.pages || 0;
        const hasMore = currentPage < totalPages;

        // Helper function to check if URL is absolute
        const isAbsoluteUrl = (url) => {
          return url && (url.startsWith('http://') || url.startsWith('https://'));
        };

        // Helper function to get full URL
        const getFullUrl = (url) => {
          if (!url) return null;
          return isAbsoluteUrl(url) ? url : `${serverBaseURL}${url}`;
        };

        const items = media.map((item) => ({
          id: item._id,
          name: item.originalName,
          thumbnail: getFullUrl(item.thumbnail) || getFullUrl(item.url),
          url: getFullUrl(item.url),
          duration: item.duration,
          width: item.dimensions?.width || item.width,
          height: item.dimensions?.height || item.height,
          type:
            item.mediaType === 'image'
              ? 'images'
              : item.mediaType === 'video'
              ? 'videos'
              : 'audio',
          category: 'AI Generated',
          description: item.description || '',
          size: item.formattedSize || item.size,
          isUserUpload: false,
          isAiGenerated: true,
          uploadedAt: item.uploadedAt,
        }));

        return {
          data: items,
          hasMore,
        };
      } catch (error) {
        console.error('Error fetching AI media:', error);
        return { data: [], hasMore: false };
      }
    },
    [searchQuery]
  );

  const [refreshKey, setRefreshKey] = useState(0);

  const handleItemDoubleClick = (item) => {
    if (onItemDoubleClick && typeof onItemDoubleClick === 'function') {
      onItemDoubleClick(item);
    }
  };

  const handleDeleteMedia = async (item) => {
    try {
      await mediaAPI.deleteMedia(item.id);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to delete media:', error);
      alert('Failed to delete media. Please try again.');
    }
  };

  return (
    <MediaRenderer
      key={searchQuery + refreshKey}
      dataFetcher={fetchAiMedia}
      pageSize={pageSize}
      gridCols={gridCols}
      className={className}
      itemType="mixed"
      dependencies={[searchQuery]}
      endMessage="No more AI generated media to load"
      showDuration={true}
      showSize={false}
      showType={true}
      onItemDoubleClick={handleItemDoubleClick}
      onDeleteItem={handleDeleteMedia}
      layout="masonry"
      targetRowHeight={130}
      justifiedGap={4}
      getAspectRatio={(item) => {
        if (item.type === 'audio') return 1;
        if (item.width && item.height) return item.width / item.height;
        if (item.type === 'videos' || item.type === 'video') return 16 / 9;
        return 4 / 3;
      }}
    />
  );
};

export default AiMedia;
